import React, { useDeferredValue, useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Link2,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { catalogApi } from '../../../services/catalogApi';
import type {
  CatalogEntity,
  CatalogEvent,
  CatalogEventSource,
  CatalogMetadata,
  EntityEventSourceMapping,
  EventSourceInput,
} from '../../../types/catalog';
import { displayEntityType } from '../../../utils/entityTerminology';
import {
  ButtonRow,
  Field,
  Input,
  Label,
  PrimaryButton,
  SecondaryButton,
} from '../AdminUI';
import { EventSourceForm } from './CatalogForms';
import {
  ActionRow,
  Badge,
  CompactActionButton,
  CompactActions,
  DataTable,
  DetailHeader,
  DetailPane,
  DetailSubtitle,
  DetailTitle,
  EmptyState,
  ErrorBanner,
  Hint,
  ListPane,
  Loading,
  PaneToolbar,
  ResultButton,
  ResultList,
  ResultMeta,
  ResultTitle,
  ResourceContent,
  ResourceDescription,
  ResourceList,
  ResourceMeta,
  ResourceRow,
  ResourceTitle,
  SearchInput,
  SearchRow,
  SearchSubmit,
  Section,
  SectionHeader,
  SectionTitle,
  SplitPane,
  TableWrap,
  formatDate,
  errorMessage,
} from './CatalogUI';

interface EventSourceManagerProps {
  metadata: CatalogMetadata;
  focusEventSourceId?: number | null;
  onOpenEntity: (entityId: number) => void;
  onOpenEvent: (eventId: number) => void;
}

export const EventSourceManager: React.FC<EventSourceManagerProps> = ({
  metadata,
  focusEventSourceId,
  onOpenEntity,
  onOpenEvent,
}) => {
  const [search, setSearch] = useState('');
  const [sources, setSources] = useState<CatalogEventSource[]>([]);
  const [selected, setSelected] = useState<CatalogEventSource | null>(null);
  const [mappings, setMappings] = useState<EntityEventSourceMapping[]>([]);
  const [events, setEvents] = useState<CatalogEvent[]>([]);
  const [pastDays, setPastDays] = useState(30);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [entitySearch, setEntitySearch] = useState('');
  const deferredEntitySearch = useDeferredValue(entitySearch);
  const entitySearchSequence = useRef(0);
  const [entitySearchRefresh, setEntitySearchRefresh] = useState(0);
  const [entitySearchLoading, setEntitySearchLoading] = useState(false);
  const [entityResults, setEntityResults] = useState<CatalogEntity[]>([]);
  const [creationEntityIds, setCreationEntityIds] = useState<number[]>([]);
  const [linkingEntity, setLinkingEntity] = useState(false);

  const loadRelations = async (eventSourceId: number, historyDays = pastDays) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const [nextMappings, nextEvents] = await Promise.all([
        catalogApi.listEntityEventSources({ eventSourceId }),
        catalogApi.listEvents({ eventSourceId, pastDays: historyDays, limit: 100 }),
      ]);
      setMappings(nextMappings);
      setEvents(nextEvents);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoadingDetail(false);
    }
  };

  const chooseSource = async (source: CatalogEventSource) => {
    setSelected(source);
    setCreating(false);
    setEditing(false);
    setLinkingEntity(false);
    await loadRelations(source.eventSourceId);
  };

  useEffect(() => {
    let active = true;
    catalogApi.listEventSources('', 50)
      .then((result) => {
        if (active) setSources(result);
      })
      .catch((loadError) => {
        if (active) setError(errorMessage(loadError));
      })
      .finally(() => {
        if (active) setLoadingList(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!focusEventSourceId || selected?.eventSourceId === focusEventSourceId) return;
    setLoadingDetail(true);
    catalogApi.getEventSource(focusEventSourceId)
      .then((source) => {
        setSources((current) => current.some((item) => item.eventSourceId === source.eventSourceId)
          ? current
          : [source, ...current]);
        return chooseSource(source);
      })
      .catch((loadError) => setError(errorMessage(loadError)))
      .finally(() => setLoadingDetail(false));
    // focusEventSourceId is the navigation signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusEventSourceId]);

  useEffect(() => {
    const query = deferredEntitySearch.trim();
    if ((!creating && !linkingEntity) || query.length < 2) {
      setEntityResults([]);
      setEntitySearchLoading(false);
      return;
    }

    let active = true;
    const sequence = ++entitySearchSequence.current;
    const timer = window.setTimeout(async () => {
      setEntitySearchLoading(true);
      try {
        const result = await catalogApi.listEntities(query, 30);
        if (active && sequence === entitySearchSequence.current) setEntityResults(result);
      } catch (searchError) {
        if (active && sequence === entitySearchSequence.current) setError(errorMessage(searchError));
      } finally {
        if (active && sequence === entitySearchSequence.current) setEntitySearchLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [creating, deferredEntitySearch, entitySearchRefresh, linkingEntity]);

  const submitSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoadingList(true);
    setError(null);
    try {
      setSources(await catalogApi.listEventSources(search, 50));
    } catch (searchError) {
      setError(errorMessage(searchError));
    } finally {
      setLoadingList(false);
    }
  };

  const findEntities = (event: React.FormEvent) => {
    event.preventDefault();
    setEntitySearchRefresh((current) => current + 1);
  };

  const toggleCreationEntity = (entityId: number) => {
    setCreationEntityIds((current) => current.includes(entityId)
      ? current.filter((id) => id !== entityId)
      : [...current, entityId]);
  };

  const createSource = async (input: EventSourceInput) => {
    setBusy(true);
    setError(null);
    try {
      const source = await catalogApi.createEventSource(input);
      await Promise.all(creationEntityIds.map((entityId) =>
        catalogApi.linkEntityEventSource(entityId, source.eventSourceId)
      ));
      setSources((current) => [source, ...current.filter((item) =>
        item.eventSourceId !== source.eventSourceId
      )]);
      setCreationEntityIds([]);
      setEntityResults([]);
      setEntitySearch('');
      await chooseSource(source);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  };

  const saveSource = async (input: EventSourceInput) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const source = await catalogApi.updateEventSource(selected.eventSourceId, input);
      setSelected(source);
      setSources((current) => current.map((item) =>
        item.eventSourceId === source.eventSourceId ? source : item
      ));
      setEditing(false);
      await loadRelations(source.eventSourceId);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  };

  const linkEntity = async (entity: CatalogEntity) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.linkEntityEventSource(entity.entityId, selected.eventSourceId);
      await catalogApi.setEntityEventSourceActive(entity.entityId, selected.eventSourceId, true);
      setLinkingEntity(false);
      setEntitySearch('');
      setEntityResults([]);
      await loadRelations(selected.eventSourceId);
    } catch (linkError) {
      setError(errorMessage(linkError));
    } finally {
      setBusy(false);
    }
  };

  const toggleMapping = async (mapping: EntityEventSourceMapping) => {
    setBusy(true);
    setError(null);
    try {
      await catalogApi.setEntityEventSourceActive(
        mapping.entityId,
        mapping.eventSourceId,
        !mapping.isActive
      );
      setMappings((current) => current.map((item) =>
        item.entityId === mapping.entityId ? { ...item, isActive: !mapping.isActive } : item
      ));
    } catch (toggleError) {
      setError(errorMessage(toggleError));
    } finally {
      setBusy(false);
    }
  };

  const unlinkEntity = async (mapping: EntityEventSourceMapping) => {
    if (!selected || !window.confirm(`Remove ${mapping.entityName} from this source?`)) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.unlinkEntityEventSource(mapping.entityId, mapping.eventSourceId);
      await loadRelations(selected.eventSourceId);
    } catch (unlinkError) {
      setError(errorMessage(unlinkError));
    } finally {
      setBusy(false);
    }
  };

  const entityPicker = (
    <Section>
      <SectionHeader>
        <SectionTitle>{creating ? 'Link entities during creation' : 'Link an entity'}</SectionTitle>
      </SectionHeader>
      <SearchRow onSubmit={findEntities}>
        <SearchInput
          aria-label="Search entities to link"
          aria-autocomplete="list"
          aria-controls="source-entity-suggestions"
          aria-expanded={entityResults.length > 0}
          role="combobox"
          value={entitySearch}
          onChange={(event) => {
            setEntitySearch(event.target.value);
            setEntityResults([]);
            setEntitySearchLoading(event.target.value.trim().length >= 2);
          }}
          placeholder="Start typing an entity name"
        />
        <SearchSubmit label="Search entities" disabled={entitySearchLoading} />
      </SearchRow>
      <Hint aria-live="polite">
        {entitySearchLoading
          ? 'Finding entities...'
          : entitySearch.trim().length < 2
            ? 'Type at least 2 characters'
            : `${entityResults.length} suggestions`}
      </Hint>
      {entityResults.length > 0 && (
        <TableWrap id="source-entity-suggestions">
          <DataTable>
            <thead><tr><th>Entity</th><th>Type</th><th>Action</th></tr></thead>
            <tbody>
              {entityResults.map((entity) => {
                const included = creationEntityIds.includes(entity.entityId);
                return (
                  <tr key={entity.entityId}>
                    <td>{entity.entityName}</td>
                    <td>{displayEntityType(entity.entityType)}</td>
                    <td>
                      {creating ? (
                        <SecondaryButton type="button" onClick={() => toggleCreationEntity(entity.entityId)}>
                          {included ? <X size={15} /> : <Plus size={15} />}
                          {included ? 'Remove' : 'Include'}
                        </SecondaryButton>
                      ) : (
                        <SecondaryButton type="button" onClick={() => linkEntity(entity)}>
                          <Link2 size={15} /> Link
                        </SecondaryButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        </TableWrap>
      )}
      {creating && creationEntityIds.length > 0 && (
        <ResultMeta>{creationEntityIds.length} entities selected</ResultMeta>
      )}
    </Section>
  );

  return (
    <SplitPane>
      <ListPane>
        <PaneToolbar>
          <SearchRow onSubmit={submitSearch}>
            <SearchInput
              aria-label="Search event sources"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search URL or source info"
            />
            <SearchSubmit label="Search event sources" disabled={loadingList} />
          </SearchRow>
          <ButtonRow>
            <PrimaryButton
              type="button"
              onClick={() => {
                setCreating(true);
                setSelected(null);
                setEditing(false);
              }}
            >
              <Plus size={16} /> New source
            </PrimaryButton>
          </ButtonRow>
        </PaneToolbar>
        {loadingList ? <Loading label="Loading event sources" /> : (
          <ResultList>
            {sources.length === 0 && <EmptyState>No event sources found.</EmptyState>}
            {sources.map((source) => (
              <ResultButton
                type="button"
                key={source.eventSourceId}
                $selected={selected?.eventSourceId === source.eventSourceId}
                onClick={() => chooseSource(source)}
              >
                <ResultTitle>{source.source}</ResultTitle>
                <ResultMeta>
                  {source.eventSourceType !== 'Website' && <Badge>{source.eventSourceType}</Badge>}
                  <span>#{source.eventSourceId}</span>
                </ResultMeta>
              </ResultButton>
            ))}
          </ResultList>
        )}
      </ListPane>

      <DetailPane>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {creating ? (
          <>
            <DetailHeader>
              <div>
                <DetailTitle>New event source</DetailTitle>
                <DetailSubtitle>Create the source and assign it to one or more entities.</DetailSubtitle>
              </div>
            </DetailHeader>
            {entityPicker}
            <Section>
              <EventSourceForm
                eventSourceTypes={metadata.eventSourceTypes}
                busy={busy}
                onSubmit={createSource}
                onCancel={() => setCreating(false)}
              />
            </Section>
          </>
        ) : !selected ? (
          <EmptyState>Select a source to manage its entities and events.</EmptyState>
        ) : loadingDetail ? (
          <Loading label="Loading source relationships" />
        ) : (
          <>
            <DetailHeader>
              <div>
                <DetailTitle>{selected.source}</DetailTitle>
                <DetailSubtitle>
                  {selected.eventSourceType !== 'Website' && `${selected.eventSourceType} · `}
                  Source #{selected.eventSourceId}
                </DetailSubtitle>
              </div>
              <ActionRow>
                <SecondaryButton type="button" onClick={() => setEditing(!editing)}>
                  <Pencil size={16} /> Edit source
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => loadRelations(selected.eventSourceId)}
                >
                  <RefreshCw size={16} /> Refresh
                </SecondaryButton>
              </ActionRow>
            </DetailHeader>

            {editing && (
              <Section>
                <SectionTitle>Source fields</SectionTitle>
                <EventSourceForm
                  eventSourceTypes={metadata.eventSourceTypes}
                  eventSource={selected}
                  busy={busy}
                  onSubmit={saveSource}
                  onCancel={() => setEditing(false)}
                />
              </Section>
            )}

            <Section>
              <SectionHeader>
                <SectionTitle>Linked entities ({mappings.length})</SectionTitle>
                <PrimaryButton type="button" onClick={() => setLinkingEntity(!linkingEntity)}>
                  <Plus size={16} /> Link entity
                </PrimaryButton>
              </SectionHeader>
              {linkingEntity && entityPicker}
              {mappings.length === 0 ? <EmptyState>No entities linked.</EmptyState> : (
                <ResourceList>
                  {mappings.map((mapping) => (
                    <ResourceRow key={mapping.entityId}>
                      <ResourceContent>
                        <ResourceTitle>{mapping.entityName}</ResourceTitle>
                        <ResourceMeta>
                          <Badge>{displayEntityType(mapping.entityType)}</Badge>
                            <Badge $tone={mapping.isActive ? 'success' : 'warning'}>
                              {mapping.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          <span>{mapping.sourceOrigin === 'manual' ? 'Manual' : 'WebIQ'}</span>
                          <span>Entity #{mapping.entityId}</span>
                        </ResourceMeta>
                      </ResourceContent>
                      <CompactActions>
                        <CompactActionButton
                          type="button"
                          onClick={() => toggleMapping(mapping)}
                          disabled={busy}
                        >
                          <Power size={14} /> {mapping.isActive ? 'Deactivate' : 'Activate'}
                        </CompactActionButton>
                        <CompactActionButton type="button" onClick={() => onOpenEntity(mapping.entityId)}>
                          <ExternalLink size={14} /> Open entity
                        </CompactActionButton>
                        <CompactActionButton
                          type="button"
                          $tone="danger"
                          onClick={() => unlinkEntity(mapping)}
                        >
                          <Trash2 size={14} /> Unlink
                        </CompactActionButton>
                      </CompactActions>
                    </ResourceRow>
                  ))}
                </ResourceList>
              )}
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Mapped events ({events.length})</SectionTitle>
                <ActionRow>
                  <Field style={{ margin: 0, width: 130 }}>
                    <Label htmlFor="source-past-days">Past days</Label>
                    <Input
                      id="source-past-days"
                      type="number"
                      min={0}
                      max={3650}
                      value={pastDays}
                      onChange={(event) => setPastDays(Number(event.target.value))}
                    />
                  </Field>
                  <SecondaryButton
                    type="button"
                    onClick={() => loadRelations(selected.eventSourceId, pastDays)}
                  >
                    <RefreshCw size={16} /> Apply
                  </SecondaryButton>
                </ActionRow>
              </SectionHeader>
              {events.length === 0 ? <EmptyState>No events in this date window.</EmptyState> : (
                <ResourceList>
                  {events.map((catalogEvent) => (
                    <ResourceRow key={catalogEvent.eventId}>
                      <ResourceContent>
                        <ResourceTitle>{catalogEvent.title}</ResourceTitle>
                        {catalogEvent.description && (
                          <ResourceDescription>{catalogEvent.description}</ResourceDescription>
                        )}
                        <ResourceMeta>
                          <span>#{catalogEvent.eventId}</span>
                          <span>{formatDate(catalogEvent.startDate)}</span>
                          {catalogEvent.type && <Badge>{catalogEvent.type}</Badge>}
                          <span>{catalogEvent.isVirtual ? 'Virtual' : [catalogEvent.city, catalogEvent.country].filter(Boolean).join(', ') || 'Location not set'}</span>
                        </ResourceMeta>
                      </ResourceContent>
                      <CompactActions>
                        <CompactActionButton type="button" onClick={() => onOpenEvent(catalogEvent.eventId)}>
                          <ExternalLink size={14} /> Open event
                        </CompactActionButton>
                      </CompactActions>
                    </ResourceRow>
                  ))}
                </ResourceList>
              )}
            </Section>
          </>
        )}
      </DetailPane>
    </SplitPane>
  );
};